import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import useModalEffect from '../../../hooks/useModalEffect';

const CreateCurrencyForm = ({ isOpen, onClose, onSubmit, initialData }) => {
  useModalEffect(isOpen);
  const [formData, setFormData] = useState(
    initialData || {
      currencyCode: '',
      currencyName: '',
      symbol: '',
      isBaseCurrency: false,
      isActive: true,
      decimalPlaces: 2
    }
  );
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        currencyCode: initialData.currencyCode || '',
        currencyName: initialData.currencyName || '',
        symbol: initialData.symbol || '',
        isBaseCurrency: initialData.isBaseCurrency || false,
        isActive: initialData.isActive !== undefined ? initialData.isActive : true,
        decimalPlaces: initialData.decimalPlaces || 2
      });
    } else {
      setFormData({
        currencyCode: '',
        currencyName: '',
        symbol: '',
        isBaseCurrency: false,
        isActive: true,
        decimalPlaces: 2
      });
    }
  }, [initialData, isOpen]);

  // Modal animation effects
  ;

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any remaining modal states when component unmounts
      const cleanupModal = () => {
        // Remove modal scroll lock
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-backdrop');
      };
      
      cleanupModal();
    };
  }, []);


  // Cleanup effect: reset modal body state when component unmounts.
  useEffect(() => {
    return () => {
      // React owns the backdrop DOM (createPortal); only reset body state here.
      // Remove modal-open class from body
      document.body.classList.remove('modal-open');
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.currencyCode.trim()) {
      errors.currencyCode = 'Currency Code is required';
    }
    if (!formData.currencyName.trim()) {
      errors.currencyName = 'Currency Name is required';
    }
    if (!formData.symbol.trim()) {
      errors.symbol = 'Symbol is required';
    }
    return errors;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length === 0) {
      onSubmit(formData);
      setFormData({
        currencyCode: '',
        currencyName: '',
        symbol: '',
        isBaseCurrency: false,
        isActive: true,
        decimalPlaces: 2
      });
    } else {
      setFormErrors(errors);
    }
  };

  const handleModalClose = () => {
    setFormData({
      currencyCode: '',
      currencyName: '',
      symbol: '',
      isBaseCurrency: false,
      isActive: true,
      decimalPlaces: 2
    });
    setFormErrors({});
    onClose();
  };

  return (
    <>
      <style>{`
        #modal-create-currency .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-create-currency .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-create-currency .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-create-currency .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-create-currency .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-create-currency .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}
        id="modal-create-currency"
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-md modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{initialData ? 'Edit Currency' : 'Create Currency'}</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="inv-section-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                  Details
                </div>
                <div className="mb-3">
                  <label className="form-label">Currency Code <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${formErrors.currencyCode ? 'is-invalid' : ''}`}
                    name="currencyCode"
                    value={formData.currencyCode}
                    onChange={handleInputChange}
                    placeholder="Enter currency code (e.g., USD)"
                  />
                  {formErrors.currencyCode && (
                    <div className="invalid-feedback">{formErrors.currencyCode}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Currency Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${formErrors.currencyName ? 'is-invalid' : ''}`}
                    name="currencyName"
                    value={formData.currencyName}
                    onChange={handleInputChange}
                    placeholder="Enter currency name (e.g., US Dollar)"
                  />
                  {formErrors.currencyName && (
                    <div className="invalid-feedback">{formErrors.currencyName}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Symbol <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${formErrors.symbol ? 'is-invalid' : ''}`}
                    name="symbol"
                    value={formData.symbol}
                    onChange={handleInputChange}
                    placeholder="Enter currency symbol (e.g., $)"
                  />
                  {formErrors.symbol && (
                    <div className="invalid-feedback">{formErrors.symbol}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Decimal Places</label>
                  <select
                    className="form-control"
                    name="decimalPlaces"
                    value={formData.decimalPlaces}
                    onChange={handleInputChange}
                  >
                    <option value={0}>0 (No decimals)</option>
                    <option value={1}>1 decimal place</option>
                    <option value={2}>2 decimal places</option>
                    <option value={3}>3 decimal places</option>
                    <option value={4}>4 decimal places</option>
                  </select>
                </div>

                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="isBaseCurrency"
                      checked={formData.isBaseCurrency}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        isBaseCurrency: e.target.checked
                      }))}
                    />
                    <label className="form-check-label">
                      Set as Base Currency (IDR)
                    </label>
                    <div className="form-hint">
                      Base currency exchange rate will be automatically set to 1.00
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        isActive: e.target.checked 
                      }))}
                    />
                    <label className="form-check-label">
                      Active Currency
                    </label>
                    <div className="form-hint">
                      Only active currencies can be used in proposals
                    </div>
                  </div>
                </div>
              </div>
              <div className="inv-footer">
                <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                  <span style={{ color: '#9aa5b1' }}>
                    <span className="inv-required-star">*</span> Required fields
                  </span>
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-inv-save">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

CreateCurrencyForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object
};

export default CreateCurrencyForm; 